import { Link } from 'react-router-dom';
import { Badge, Button, ReviewTypeLogo } from '../ui';
import { Calendar, Users } from 'lucide-react';
import type { Task } from '../../api/client';

interface TaskListCardProps {
  task: Task;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function getParticipationBadge(status: string): { variant: 'blue' | 'yellow' | 'green' | 'red'; label: string } {
  switch (status) {
    case 'claimed': return { variant: 'blue', label: 'Claimed' };
    case 'submitted': return { variant: 'yellow', label: 'Submitted' };
    case 'approved': return { variant: 'green', label: 'Approved' };
    case 'rejected': return { variant: 'red', label: 'Rejected' };
    default: return { variant: 'blue', label: status };
  }
}

export function TaskListCard({ task }: TaskListCardProps) {
  const slotsLeft = task.max_participants - task.participant_count;
  const isCompleted = task.status === 'completed';
  const isExpired = task.status === 'expired' || (task.status === 'active' && new Date(task.end_date) < new Date());
  const userStatus = task.user_participation_status;

  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-3 hover:shadow-md transition-all duration-200 h-full ${isCompleted || isExpired ? 'opacity-50 grayscale' : ''}`}>
      {/* Top Row: Status + Credits */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Badge
            variant={
              isExpired ? 'red' :
              isCompleted ? 'blue' :
              task.status === 'active' ? 'green' : 'gray'
            }
          >
            {isExpired ? 'Expired' : task.status === 'active' ? 'Active' : task.status === 'completed' ? 'Completed' : task.status}
          </Badge>
          {userStatus && (
            <Badge variant={getParticipationBadge(userStatus).variant}>
              {getParticipationBadge(userStatus).label}
            </Badge>
          )}
        </div>
        <span className="text-sm font-semibold text-primary-600">{task.credit_reward} credits</span>
      </div>

      {/* Type Row: Logo + Label */}
      <div className="flex items-center gap-2">
        <ReviewTypeLogo type={task.review_type || ''} size="sm" />
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {task.review_type === 'google_review' ? 'Google Review' : task.review_type === 'facebook_like' ? 'Facebook' : 'Review'}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-base font-semibold text-gray-900 line-clamp-1">{task.name}</h3>

      {/* Description */}
      {task.instructions && (
        <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">{task.instructions}</p>
      )}

      {/* Meta Row */}
      <div className="flex items-center gap-4 text-xs text-gray-500 mt-auto pt-1">
        <span className="flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-gray-400" />
          {slotsLeft}/{task.max_participants} slots
        </span>
        <span className="flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 text-gray-400" />
          Ends {formatDate(task.end_date)}
        </span>
      </div>

      {/* CTA Button */}
      <Link to={`/tasks/${task.id}`} className="block mt-1">
        <Button
          variant={isCompleted || isExpired || userStatus === 'approved' || userStatus === 'rejected' ? 'secondary' : 'primary'}
          className="w-full"
        >
          {isCompleted || isExpired ? 'View Details' :
           userStatus === 'claimed' || userStatus === 'submitted' ? 'View Progress' :
           userStatus === 'approved' || userStatus === 'rejected' ? 'View Details' :
           'View Task'}
        </Button>
      </Link>
    </div>
  );
}
